import React from "react";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import Immutable from "immutable";
import { connect } from "react-redux";
import {
  undoGreeting as undoGreetingAction,
  updateGreetingConfig as updateGreetingConfigAction,
  updateGreetingMessage as updateGreetingMessageAction,
  updateEmoji as updateEmojiAction,
  addGreetingPath as addGreetingPathAction,
  deleteGreetingPath as deleteGreetingPathAction,
  deleteLanguage as deleteLanguageAction
} from "components/Pages/Settings/SettingsGreetings/actions";
import { getResources } from "selectors/resources";
import { trimTrailingSlashes } from "services/strings";
import isURL from "validator/lib/isURL";
import { Language } from "resourceModels";
import { VALID_CHARACTER_AMOUNT } from "constants/greetings";
import GreetingType from "./GreetingType";
import GreetingMessageBody from "./GreetingMessageBody";
import GreetingResponseSelector from "./GreetingResponseSelector";
import GreetingSlider from "./GreetingSlider";
import GreetingPathList from "./GreetingPathList";
import GreetingVideoPreview from "./GreetingVideoPreview";
import GreetingFooter from "./GreetingFooter";
import "./styles.scss";

class Greeting extends React.Component {
  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.introContentRef = React.createRef();

    this.state = {
      selectedEmoji: "Hand-wave",
      path: "",
      invalidPath: false,
      clicked: true,
      currentCharCount: ""
    };

    this.emojiList = Immutable.Map({
      "Hand-wave": "https://static.ada.support/GreetingEmojis/HandWave.svg",
      "BellColoured": "https://static.ada.support/GreetingEmojis/Bell.svg",
      "Megaphon": "https://static.ada.support/GreetingEmojis/Megaphone.svg",
      "Light": "https://static.ada.support/GreetingEmojis/Light.svg",
      "Robot": "https://static.ada.support/GreetingEmojis/Robot.svg",
      "RocketShip": "https://static.ada.support/GreetingEmojis/Rocket.svg"
    });

    this.type = "UPDATE_GREETING";
    this.setActiveLanguage = this.setActiveLanguage.bind(this);
    this.updateNewPath = this.updateNewPath.bind(this);
    this.handleNewPathKeyPress = this.handleNewPathKeyPress.bind(this);
    this.handleEmojiSelect = this.handleEmojiSelect.bind(this);
    this.getValue = this.getValue.bind(this);
    this.handleStyleSelect = this.handleStyleSelect.bind(this);
    this.handleMessageInput = this.handleMessageInput.bind(this);
    this.handleResponseSelector = this.handleResponseSelector.bind(this);
    this.handleDelayInput = this.handleDelayInput.bind(this);
    this.handleDurationInput = this.handleDurationInput.bind(this);
    this.addNewPath = this.addNewPath.bind(this);
    this.deletePath = this.deletePath.bind(this);
    this.handlePreviewClick = this.handlePreviewClick.bind(this);
    this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
  }

  /**
   * Lifecycle Methods
   */

  /** */
  componentDidMount() {
    const { responses } = this.props;

    const selectedEmoji = this.emojiList
      .filter(value => value === this.getValue("body", "emojiLink"))
      .keySeq()
      .first();

    if (selectedEmoji) {
      this.setState({
        selectedEmoji
      });
    }

    const response = responses.find(resp => resp.id === this.getValue("responseId"));

    if (Boolean(this.getValue("responseId")) && !response) {
      this.handleResponseSelector(null);
    }

    // Wrapped in a setTimeout to make sure this gets called before the initial
    // render
    setTimeout(() => {
      document.activeElement.blur();

      if (this.introContentRef.current) {
        this.introContentRef.current.scrollTop = 0;
      }
    }, 0);
  }

  /** */
  componentWillUnmount() {
    const { undoGreeting } = this.props;

    undoGreeting(this.getValue("id"));
  }

  /**
   * Properties
   */

  /**
   * @returns {BaseGreeting}
   */
  get greeting() {
    const { greetings, index } = this.props;

    return greetings.get(index);
  }

  /**
   * Returns the value in the current intro associated with the given key(s)
   *
   * @param  {String} keys
   * @returns {any}
   */
  getValue(...keys) {
    const { greetings, index } = this.props;

    return greetings.getIn([index, ...keys]);
  }

  /**
   * Gets the currently active language
   *
   * @returns {String}
   */
  getCurrentLanguageName() {
    const { languages } = this.props;

    return languages
      .find(language => language.code === this.getValue("activeLanguage"))
      .englishName;
  }

  /**
   * Sets the chosen language as active
   *
   * @param {String} languageCode
   */
  setActiveLanguage(languageCode) {
    const { updateGreetingConfig } = this.props;
    updateGreetingConfig(this.getValue("id"), "activeLanguage", languageCode);
  }

  /**
   * Sets current path input
   *
   * @param {String} newPath
   */
  updateNewPath(newPath) {
    this.setState({ "path": newPath });
    this.validatePath(newPath);
  }

  /**
   * Sets the greeting to the delay slider's current value
   *
   * @param {Event} event
   */
  handleDelayInput(event) {
    const { updateGreetingConfig } = this.props;
    updateGreetingConfig(this.getValue("id"), "delay", event.target.value);
  }

  /**
   * Sets the greeting to the duration slider's current value
   *
   * @param {Event} event
   */
  handleDurationInput(event) {
    const { updateGreetingConfig } = this.props;
    updateGreetingConfig(this.getValue("id"), "duration", event.target.value);
  }

  /**
   * Sets the greeting to the message text input's current value
   *
   * @param {Event} event
   */
  handleMessageInput(event) {
    const { updateGreetingMessage } = this.props;
    updateGreetingMessage(this.getValue("id"), event, this.getValue("activeLanguage"));
    this.setState({ currentCharCount: event.length });
  }

  /**
   * Sets the greeting to the message text input's current value
   *
   * @param {String} emoji
   */
  handleEmojiSelect(emoji) {
    const { updateEmoji } = this.props;
    const emojiURL = this.emojiList.get(emoji);
    this.setState({
      selectedEmoji: emoji,
      clicked: true
    });
    updateEmoji(this.getValue("id"), emojiURL);
  }

  /**
   * Runs the emoji preview animation when the preview play button is clicked
   */
  handlePreviewClick() {
    this.setState({
      clicked: true
    });
  }

  /**
   * Changes the greeting type
   *
   * @param {String} style
   */
  handleStyleSelect(style) {
    const { updateGreetingConfig } = this.props;
    updateGreetingConfig(this.getValue("id"), "style", style);
    this.setState({
      clicked: false
    });
  }

  /**
   * Changes the selected follow-up answer
   *
   * @param {Response|null} response
   */
  handleResponseSelector(response) {
    const { updateGreetingConfig } = this.props;
    updateGreetingConfig(this.getValue("id"), "responseId", response ? response.id : null);
  }

  /**
   * Adds a new path to the greeting
   *
   * @param {String} path
   */
  addNewPath(path) {
    const { addGreetingPath } = this.props;
    addGreetingPath(this.getValue("id"), path);
    this.setState({
      path: ""
    });
  }

  /**
   * Wrapper for addNewPath() to handle Enter key press
   *
   * @param {Event} event
   */
  handleNewPathKeyPress(event) {
    const {
      path,
      invalidPath
    } = this.state;

    if (event.key === "Enter" && path !== "" && !invalidPath) {
      this.addNewPath(path);
    }
  }

  /**
   * Removes a path from the greeting
   *
   * @param {String} key
   */
  deletePath(key) {
    const { deleteGreetingPath } = this.props;
    deleteGreetingPath(this.getValue("id"), key);
  }

  /**
   * Resets clicked state when emoji preview animation ends
   */
  handleAnimationEnd() {
    this.setState({
      clicked: false
    });
  }

  /**
   * Checks to see if newPath is a valid HTTP link and not already in use
   *
   * @param {String} newPath
   */
  validatePath(newPath) {
    const { path } = this.state;

    // We want to allow wildcard characters in these URL's
    const escapedPath = newPath.replace("*", "valid");
    const isValidURL = isURL(escapedPath);

    const inUse = this.pathAlreadyInUse(newPath);

    if (
      (newPath.indexOf(" ") !== -1) ||
      (!isValidURL && path) ||
      inUse
    ) {
      this.setState({
        invalidPath: true,
        pathInUse: inUse
      });

      return;
    }

    this.setState({
      invalidPath: false,
      pathInUse: false
    });
  }

  /**
   * Checks if newPath already exists in greetings
   *
   * @param {String} newPath
   *
   * @returns {Boolean}
   */
  pathAlreadyInUse(newPath) {
    const { greetings } = this.props;

    return greetings.some(greeting => greeting.get("paths").some(path => (
      trimTrailingSlashes(path) === trimTrailingSlashes(newPath)
    )));
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      responses,
      tags,
      index
    } = this.props;

    const {
      selectedEmoji,
      path,
      pathInUse,
      invalidPath,
      currentCharCount,
      clicked
    } = this.state;

    return (
      <div className="Greeting__view">
        <h5 className="Modal__title">
          Intro Settings
        </h5>
        <div
          className="Greeting__greetings-wrapper Modal__content"
          ref={this.introContentRef}
        >
          <div className="Greeting__left-column">
            <GreetingType
              handleStyleSelect={this.handleStyleSelect}
              getValue={this.getValue}
            />
            <GreetingMessageBody
              greeting={this.greeting}
              getValue={this.getValue}
              selectedEmoji={selectedEmoji}
              emojiList={this.emojiList}
              handleEmojiSelect={this.handleEmojiSelect}
              setActiveLanguage={this.setActiveLanguage}
              handleMessageInput={this.handleMessageInput}
            />
            <GreetingResponseSelector
              responses={responses}
              tags={tags}
              getValue={this.getValue}
              handleResponseSelector={this.handleResponseSelector}
              introContentRef={this.introContentRef}
            />
            <GreetingSlider
              sliderType="delay"
              getValue={this.getValue}
              handleSliderInput={this.handleDelayInput}
            />
            <GreetingSlider
              sliderType="duration"
              getValue={this.getValue}
              handleSliderInput={this.handleDurationInput}
            />
            <GreetingPathList
              path={path}
              pathInUse={pathInUse}
              invalid={invalidPath}
              updateNewPath={this.updateNewPath}
              handleNewPathKeyPress={this.handleNewPathKeyPress}
              addNewPath={this.addNewPath}
              getValue={this.getValue}
              deletePath={this.deletePath}
            />
          </div>
          <GreetingVideoPreview
            clicked={clicked}
            handlePreviewClick={this.handlePreviewClick}
            getValue={this.getValue}
            selectedEmoji={selectedEmoji}
            handleAnimationEnd={this.handleAnimationEnd}
          />
        </div>
        <GreetingFooter
          getValue={this.getValue}
          index={index}
          isValidCharacterAmount={VALID_CHARACTER_AMOUNT >= currentCharCount}
        />
      </div>
    );
  }
}

Greeting.propTypes = {
  index: PropTypes.number.isRequired,
  responses: ImmutablePropTypes.list.isRequired,
  languages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)).isRequired,
  greetings: ImmutablePropTypes.list.isRequired,
  tags: ImmutablePropTypes.list.isRequired
};

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapState(state) {
  return {
    responses: state.responses, // TODO BUIL-489: fetch these if they aren't in Redux already
    languages: getResources(state, "language"),
    greetings: state.greetings,
    tags: state.tags
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object}
 */
function mapDispatch(dispatch) {
  return bindActionCreators({
    undoGreeting: undoGreetingAction,
    updateGreetingConfig: updateGreetingConfigAction,
    updateGreetingMessage: updateGreetingMessageAction,
    updateEmoji: updateEmojiAction,
    addGreetingPath: addGreetingPathAction,
    deleteGreetingPath: deleteGreetingPathAction,
    deleteLanguage: deleteLanguageAction
  }, dispatch);
}

const Connector = connect(mapState, mapDispatch)(Greeting);

export default Connector;
export { Greeting };
